import { useMemo, useRef, useState, useEffect } from "react";
import { motion } from "framer-motion";
import { toPng } from "html-to-image";
import { computeStandings, formatMoney, holePayout } from "@/lib/game";
import { saveRecentRound } from "@/lib/recentRounds";
import Scorecard from "@/components/Scorecard";
import ShareCard from "@/components/ShareCard";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function ShareIcon() {
    return (
        <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            style={{ marginRight: 8 }}
        >
            <path d="M4 12v7a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-7" />
            <polyline points="16 6 12 2 8 6" />
            <line x1="12" y1="2" x2="12" y2="15" />
        </svg>
    );
}

export default function SummaryScreen({
    roundId,
    players,
    course,
    holes,
    onNewRound,
    onBack,
}) {
    useEffect(() => {
        if (roundId && players && course && holes) {
            saveRecentRound({
                id: roundId,
                players,
                course,
                holes
            });
            // Try saving to database as backup
            axios.post(`${API}/rounds`, {
                players,
                course,
                holes,
                courseLabel: course?.label
            }).catch(() => {});
        }
    }, [roundId, players, course, holes]);
    const startingBet = course?.startingBet ?? 3;
    const eagleEnabled = course?.eagleEnabled ?? true;
    const standings = useMemo(
        () => computeStandings({ players, holes, startingBet }),
        [players, holes, startingBet],
    );
    const ordered = useMemo(() => {
        return players
            .map((p, i) => ({
                idx: i,
                name: p.name || `Player ${i + 1}`,
                winnings: standings[i],
            }))
            .sort((a, b) => b.winnings - a.winnings);
    }, [players, standings]);

    const totalWagered = holes.reduce(
        (a, _, i) => a + holePayout(i, holes, startingBet),
        0,
    );
    const birdieCount = holes.filter((h) => h.result?.birdie).length;
    const eagleCount = holes.filter((h) => h.result?.eagle).length;
    const tiedCount = holes.filter((h) => h.result?.status === "tied").length;

    const shareRef = useRef(null);
    const [sharing, setSharing] = useState(false);

    const buildShareBlob = async () => {
        if (!shareRef.current) return null;
        const dataUrl = await toPng(shareRef.current, {
            cacheBust: true,
            pixelRatio: 2,
            backgroundColor: "#08090A",
            width: 1080,
            height: 1350,
        });
        const res = await fetch(dataUrl);
        return res.blob();
    };

    const winnerName = ordered[0]?.name || "Winner";
    const filename = `fairway-stakes-${winnerName.toLowerCase().replace(/\s+/g, "-")}-${Date.now()}.png`;

    const shareOrDownload = async () => {
        setSharing(true);
        try {
            const blob = await buildShareBlob();
            if (!blob) throw new Error("card not ready");
            const file = new File([blob], filename, { type: "image/png" });
            const shareData = { files: [file], title: "Fairway Stakes" };
            if (
                navigator.canShare &&
                navigator.canShare(shareData) &&
                navigator.share
            ) {
                await navigator.share(shareData);
            } else {
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = filename;
                document.body.appendChild(a);
                a.click();
                a.remove();
                setTimeout(() => URL.revokeObjectURL(url), 400);
                toast.success("Share card downloaded.");
            }
        } catch (e) {
            if (e?.name !== "AbortError") {
                toast.error("Couldn't build share card. Try again.");
            }
        } finally {
            setSharing(false);
        }
    };

    return (
        <div className="min-h-screen" data-testid="summary-screen">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-10 pb-20">
                <div className="flex items-center justify-between mb-8">
                    <div>
                        <div className="eyebrow">Round complete</div>
                        <h1 className="font-display text-4xl sm:text-5xl font-[800] mt-1">
                            Settle up.
                        </h1>
                    </div>
                    <button
                        onClick={onBack}
                        data-testid="back-to-play-button"
                        className="text-[color:var(--smoke)] hover:text-[color:var(--bone)] text-xs uppercase tracking-[0.2em]"
                    >
                        ← Back
                    </button>
                </div>

                {/* Podium */}
                <div className="grid gap-3 mb-8">
                    {ordered.map((row, rank) => (
                        <motion.div
                            key={row.idx}
                            initial={{ opacity: 0, y: 6 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: rank * 0.05, duration: 0.25 }}
                            className="surface hairline p-4 sm:p-5 flex items-center justify-between"
                            style={{
                                borderRadius: 3,
                                borderLeft:
                                    rank === 0
                                        ? "3px solid var(--volt)"
                                        : "1px solid var(--line)",
                            }}
                            data-testid={`summary-standing-${rank}`}
                        >
                            <div className="flex items-center gap-4">
                                <div
                                    className="w-12 h-12 flex items-center justify-center border hairline-strong font-mono text-lg"
                                    style={{
                                        borderRadius: 3,
                                        color:
                                            rank === 0
                                                ? "var(--volt)"
                                                : "var(--smoke)",
                                    }}
                                >
                                    #{rank + 1}
                                </div>
                                <div>
                                    <div className="font-display text-2xl font-[700] uppercase tracking-wide">
                                        {row.name}
                                    </div>
                                    <div className="text-[color:var(--smoke)] text-xs uppercase tracking-[0.2em] mt-1">
                                        HCP{" "}
                                        <span className="font-mono text-[color:var(--bone)]">
                                            {players[row.idx].handicap}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div className="text-right">
                                <div className="eyebrow">Net</div>
                                <div
                                    className={`font-mono text-3xl sm:text-4xl mt-1 ${
                                        row.winnings > 0
                                            ? "text-[color:var(--volt)]"
                                            : row.winnings < 0
                                              ? "text-[color:var(--danger)]"
                                              : "text-[color:var(--bone)]"
                                    }`}
                                >
                                    {row.winnings > 0 ? "+" : ""}
                                    {formatMoney(row.winnings)}
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>

                {/* Stats */}
                <div
                    className={`grid grid-cols-2 ${eagleEnabled ? "sm:grid-cols-4" : "sm:grid-cols-3"} gap-2 mb-8`}
                >
                    <div
                        className="surface hairline p-4"
                        style={{ borderRadius: 3 }}
                    >
                        <div className="eyebrow">Wagered</div>
                        <div className="font-mono text-2xl text-[color:var(--bone)] mt-1">
                            ${totalWagered}
                        </div>
                    </div>
                    {eagleEnabled && (
                        <div
                            className="surface hairline p-4"
                            style={{ borderRadius: 3 }}
                            data-testid="stat-eagles"
                        >
                            <div className="eyebrow">Eagles</div>
                            <div className="font-mono text-2xl mt-1" style={{ color: "var(--eagle)" }}>
                                {eagleCount}
                            </div>
                        </div>
                    )}
                    <div
                        className="surface hairline p-4"
                        style={{ borderRadius: 3 }}
                    >
                        <div className="eyebrow">Birdies</div>
                        <div className="font-mono text-2xl text-[color:var(--gold)] mt-1">
                            {birdieCount}
                        </div>
                    </div>
                    <div
                        className="surface hairline p-4"
                        style={{ borderRadius: 3 }}
                    >
                        <div className="eyebrow">Voided</div>
                        <div className="font-mono text-2xl text-[color:var(--bone)] mt-1">
                            {tiedCount}
                        </div>
                    </div>
                </div>

                <div className="mb-8">
                    <div className="eyebrow mb-3">Full scorecard</div>
                    <Scorecard players={players} course={course} holes={holes} />
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                    <Button
                        onClick={shareOrDownload}
                        disabled={sharing}
                        className="btn-volt inline-flex items-center justify-center"
                        data-testid="share-card-button"
                    >
                        <ShareIcon />
                        {sharing ? "Building card…" : "Share card"}
                    </Button>
                    <Button
                        onClick={onNewRound}
                        className="btn-ghost"
                        data-testid="new-round-button"
                    >
                        New round
                    </Button>
                    <Button
                        onClick={onBack}
                        className="btn-ghost"
                        data-testid="review-round-button"
                    >
                        Review holes
                    </Button>
                </div>
            </div>

            {/* Offscreen share card — rendered but not visible. */}
            <div
                aria-hidden="true"
                style={{
                    position: "fixed",
                    left: -10000,
                    top: 0,
                    pointerEvents: "none",
                    zIndex: -1,
                }}
            >
                <ShareCard
                    ref={shareRef}
                    players={players}
                    course={course}
                    holes={holes}
                    standings={standings}
                    ordered={ordered}
                    courseLabel={course?.label}
                />
            </div>
        </div>
    );
}
